import type { AnnotationRecordResponse } from "@/hooks/useLabelingQueries";
import type { StudyMetadataResponse } from "@/utils/dicomMetadataLoader";
import type { PreviewFrameGeometry } from "@/utils/dicomPreviewFrame";
import { buildSavedSegmentationPreview } from "@/predictions/savedSegmentationPreview";
import { cross, dot, savedVolumeSampler, sampleNativePlane } from "@/predictions/resampleSavedCard";

export type LoadPreviewGeometry = (
  seriesId: string,
  sliceIndex: number,
) => Promise<PreviewFrameGeometry>;

/** Canonical metadata locates the plane; the decoded frame supplies its true in-plane origin. */
export async function resolveSavedSegmentationPreview(
  annotation: AnnotationRecordResponse,
  metadata: StudyMetadataResponse | null | undefined,
  condition: string,
  sequence: number,
  loadGeometry: LoadPreviewGeometry,
  resampleVolume = false,
) {
  if (resampleVolume && annotation.card_geometry?.volume) {
    return resolveNativeSavedPreview(annotation, metadata, condition, sequence, loadGeometry);
  }
  const plane = annotation.card_geometry?.representative;
  const affine = plane?.affine;
  const seriesId = annotation.series_instance_uid;
  const series = metadata?.series_metadata[seriesId];
  const instances = metadata?.series[seriesId];
  if (
    !plane ||
    !affine ||
    affine.length !== 12 ||
    !affine.every(Number.isFinite) ||
    !series ||
    !instances
  )
    return null;
  const anchor = [0, 4, 8].map(
    (offset) => affine[offset + 2] * plane.sliceIndex + affine[offset + 3],
  );
  const candidates = instances
    .flatMap((instance, sliceIndex) => {
      const iop = instance.image_orientation_patient ?? series.image_orientation_patient;
      if (iop?.length !== 6 || !iop.every(Number.isFinite)) return [];
      const [a, b, c, d, e, f] = iop;
      const normal = [b * f - c * e, c * d - a * f, a * e - b * d];
      const project = (point: number[]) =>
        point.reduce((sum, value, axis) => sum + value * normal[axis], 0);
      const position =
        instance.image_position_patient?.length === 3
          ? project(instance.image_position_patient)
          : instance.z_position;
      if (position == null || !Number.isFinite(position)) return [];
      return [{ sliceIndex, distance: Math.abs(project(anchor) - position) }];
    })
    .sort((a, b) => a.distance - b.distance);
  const candidate = candidates[0];
  if (!candidate) return null;
  const { sliceIndex } = candidate;
  const geometry = await loadGeometry(seriesId, sliceIndex);
  const correctedMetadata: StudyMetadataResponse = {
    ...metadata!,
    series: {
      ...metadata!.series,
      [seriesId]: instances.map((instance, index) =>
        index === sliceIndex ? { ...instance, ...geometry } : instance,
      ),
    },
    series_metadata: {
      ...metadata!.series_metadata,
      [seriesId]: {
        ...series,
        image_orientation_patient: geometry.image_orientation_patient ?? null,
        pixel_spacing: geometry.pixel_spacing ?? null,
      },
    },
  };
  return buildSavedSegmentationPreview(
    annotation,
    correctedMetadata,
    condition,
    sequence,
    sliceIndex,
  );
}

async function resolveNativeSavedPreview(
  annotation: AnnotationRecordResponse,
  metadata: StudyMetadataResponse | null | undefined,
  condition: string,
  sequence: number,
  loadGeometry: LoadPreviewGeometry,
) {
  const card = annotation.card_geometry!;
  const sampler = savedVolumeSampler(card.volume!);
  const seriesId = annotation.series_instance_uid;
  const series = metadata?.series_metadata[seriesId];
  const instances = metadata?.series[seriesId];
  if (!sampler || !metadata || !series || !instances) return null;
  let best: {
    sliceIndex: number;
    frame: PreviewFrameGeometry;
    plane: NonNullable<ReturnType<typeof sampleNativePlane>>;
  } | null = null;
  for (let sliceIndex = 0; sliceIndex < instances.length; sliceIndex++) {
    const instance = instances[sliceIndex];
    const iop = instance.image_orientation_patient ?? series.image_orientation_patient;
    // Cached in-plane origins can be synthetic. Only use the normal coordinate
    // to reject non-intersecting frames; decode the actual DICOM before sampling.
    if (iop?.length === 6 && instance.image_position_patient?.length === 3) {
      const normal = cross(iop.slice(0, 3), iop.slice(3));
      const projections = sampler.corners.map((p) => dot(p, normal));
      const position = dot(instance.image_position_patient, normal);
      if (
        position < Math.min(...projections) - 0.001 ||
        position > Math.max(...projections) + 0.001
      )
        continue;
    }
    const frame = await loadGeometry(seriesId, sliceIndex);
    const plane = sampleNativePlane(sampler, frame);
    if (plane && plane.count > (best?.plane.count ?? 0)) best = { sliceIndex, frame, plane };
  }
  if (!best) return null;
  const { sliceIndex, frame, plane } = best;
  return buildSavedSegmentationPreview(
    {
      ...annotation,
      card_geometry: {
        ...card,
        representative: {
          sliceIndex: 0,
          rows: frame.rows!,
          columns: frame.columns!,
          affine: plane.affine,
          runs: plane.runs,
        },
      },
    },
    {
      ...metadata,
      series: {
        ...metadata.series,
        [seriesId]: instances.map((instance, i) =>
          i === sliceIndex ? { ...instance, ...frame } : instance,
        ),
      },
      series_metadata: {
        ...metadata.series_metadata,
        [seriesId]: {
          ...series,
          image_orientation_patient: frame.image_orientation_patient ?? null,
          pixel_spacing: frame.pixel_spacing ?? null,
        },
      },
    },
    condition,
    sequence,
    sliceIndex,
  );
}
